/**
 * 날짜 피커 공통 유틸 (저장: YYYYMMDD 권장, 표시: YYYY-MM-DD)
 * @see docs/DATE_PICKER_UX_SPEC.md
 */

export function toDisplayDate(dateStr) {
  if (!dateStr) return '';
  const s = String(dateStr).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (/^\d{8}$/.test(s)) {
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  }
  return s;
}

/** Form/API 저장용 YYYYMMDD */
export function toStorageDate(dateStr) {
  if (!dateStr) return '';
  const s = String(dateStr).trim().replace(/\./g, '-');
  if (/^\d{8}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s.replace(/-/g, '');
  const digits = s.replace(/\D/g, '');
  if (digits.length === 8) return digits;
  return '';
}

export function parseDate(dateStr) {
  const stor = toStorageDate(dateStr);
  if (!stor || stor.length !== 8) return null;
  const y = Number(stor.slice(0, 4));
  const m = Number(stor.slice(4, 6)) - 1;
  const d = Number(stor.slice(6, 8));
  const dt = new Date(y, m, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m || dt.getDate() !== d) return null;
  return dt;
}

export function formatDate(dt) {
  if (!(dt instanceof Date) || Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
}

export function todayStorage() {
  return formatDate(new Date());
}

export function compareStorage(a, b) {
  const sa = toStorageDate(a);
  const sb = toStorageDate(b);
  if (!sa || !sb) return 0;
  if (sa < sb) return -1;
  if (sa > sb) return 1;
  return 0;
}

export function isDisabledDate(dateStr, min, max) {
  const s = toStorageDate(dateStr);
  if (!s) return true;
  if (min && compareStorage(s, min) < 0) return true;
  if (max && compareStorage(s, max) > 0) return true;
  return false;
}

/**
 * M3: 시작·종료 교차 검증
 * @returns {{ ok: boolean, reason?: string }}
 */
export function validateDateOrder(start, end, { allowEmptyEnd = true } = {}) {
  const s = toStorageDate(start);
  const e = toStorageDate(end);
  if (!s && !e) return { ok: true };
  if (!s && e) return { ok: false, reason: '시작일을 입력하세요.' };
  if (s && !e) return allowEmptyEnd ? { ok: true } : { ok: false, reason: '종료일을 입력하세요.' };
  if (compareStorage(s, e) > 0) {
    return { ok: false, reason: '시작일은 종료일보다 이후일 수 없습니다.' };
  }
  return { ok: true };
}

export function addDays(storageDate, days) {
  const dt = parseDate(storageDate);
  if (!dt) return '';
  dt.setDate(dt.getDate() + days);
  return formatDate(dt);
}

export function startOfMonth(dt = new Date()) {
  return new Date(dt.getFullYear(), dt.getMonth(), 1);
}

export function endOfMonth(dt = new Date()) {
  return new Date(dt.getFullYear(), dt.getMonth() + 1, 0);
}

export function startOfQuarter(dt = new Date()) {
  const q = Math.floor(dt.getMonth() / 3);
  return new Date(dt.getFullYear(), q * 3, 1);
}

export function endOfQuarter(dt = new Date()) {
  const q = Math.floor(dt.getMonth() / 3);
  return new Date(dt.getFullYear(), q * 3 + 3, 0);
}

/** 전월 1일 */
export function startOfPrevMonth(dt = new Date()) {
  return new Date(dt.getFullYear(), dt.getMonth() - 1, 1);
}

/** 전월 말일 */
export function endOfPrevMonth(dt = new Date()) {
  return new Date(dt.getFullYear(), dt.getMonth(), 0);
}

/** 지정 연도·분기(1~4) 구간 */
export function quarterRange(year, quarter) {
  const q = Math.min(4, Math.max(1, Number(quarter) || 1));
  const from = new Date(year, (q - 1) * 3, 1);
  const to = new Date(year, (q - 1) * 3 + 3, 0);
  return { from: formatDate(from), to: formatDate(to) };
}

/**
 * From~To 포함 일수 (동일일이면 1)
 * @returns {number}
 */
export function inclusiveDayCount(from, to) {
  const a = parseDate(from);
  const b = parseDate(to);
  if (!a || !b) return 0;
  const lo = a <= b ? a : b;
  const hi = a <= b ? b : a;
  return Math.round((hi - lo) / 86400000) + 1;
}

/**
 * M1 기간 프리셋
 * @param {Date} [now]
 * @param {object} [options]
 * @param {'inquiry'|'minimal'} [options.presetSet='inquiry'] — inquiry=목업 2행, minimal=축소
 * @param {number|string} [options.fiscalYear] — 분기·반기·~오늘 연도 기준
 * @param {string} [options.anchorFrom] — 「~ 오늘」시작일 (YYYYMMDD). 없으면 기준연도 1/1
 * @param {'rolling'|'calendar'} [options.yearPreset='calendar'] — 1년: 달력 연도(1/1~12/31) / 롤링 365일
 * @returns {Array<{ id, label, from, to, row }>}
 */
export function buildRangePresets(now = new Date(), options = {}) {
  const {
    presetSet = 'inquiry',
    fiscalYear,
    anchorFrom,
    yearPreset = 'calendar',
  } = options;
  const t = formatDate(now);
  const yest = addDays(t, -1);
  const baseYear =
    fiscalYear != null && String(fiscalYear).trim() !== '' && Number.isFinite(Number(fiscalYear))
      ? Number(fiscalYear)
      : now.getFullYear();
  const yearStart = `${baseYear}0101`;
  const untilFrom = toStorageDate(anchorFrom) || yearStart;

  if (presetSet === 'minimal') {
    return [
      { id: 'today', label: '오늘', from: t, to: t, row: 1 },
      { id: 'yesterday', label: '어제', from: yest, to: yest, row: 1 },
      {
        id: 'month',
        label: '당월',
        from: formatDate(startOfMonth(now)),
        to: formatDate(endOfMonth(now)),
        row: 1,
      },
    ];
  }

  // inquiry (목업) — 기본
  const yearFromTo =
    yearPreset === 'calendar'
      ? { from: `${baseYear}0101`, to: `${baseYear}1231` }
      : { from: addDays(t, -364), to: t };

  return [
    { id: 'today', label: '오늘', from: t, to: t, row: 1 },
    { id: 'yesterday', label: '어제', from: yest, to: yest, row: 1 },
    {
      id: 'month',
      label: '당월',
      from: formatDate(startOfMonth(now)),
      to: formatDate(endOfMonth(now)),
      row: 1,
    },
    {
      id: 'prevMonth',
      label: '전월',
      from: formatDate(startOfPrevMonth(now)),
      to: formatDate(endOfPrevMonth(now)),
      row: 1,
    },
    { id: 'untilToday', label: '~ 오늘', from: untilFrom, to: t, row: 1 },
    { id: 'year1', label: '1년', from: yearFromTo.from, to: yearFromTo.to, row: 1 },
    { id: 'q1', label: '1분기', ...quarterRange(baseYear, 1), row: 2 },
    { id: 'q2', label: '2분기', ...quarterRange(baseYear, 2), row: 2 },
    { id: 'q3', label: '3분기', ...quarterRange(baseYear, 3), row: 2 },
    { id: 'q4', label: '4분기', ...quarterRange(baseYear, 4), row: 2 },
    { id: 'h1', label: '상반기', from: `${baseYear}0101`, to: `${baseYear}0630`, row: 2 },
    { id: 'h2', label: '하반기', from: `${baseYear}0701`, to: `${baseYear}1231`, row: 2 },
  ];
}

/**
 * M2 단일(기준일·발령일자) 프리셋 — 기간 프리셋과 동일 라벨.
 * 팝오버 From~To에는 구간을 그대로 채우고, 적용 시에만 to를 화면에 반영한다.
 * @returns {Array<{ id, label, from, to, date, row }>}
 */
export function buildSinglePresets(now = new Date(), options = {}) {
  return buildRangePresets(now, options).map((p) => ({
    id: p.id,
    label: p.label,
    from: p.from,
    to: p.to,
    date: p.to,
    row: p.row || 1,
  }));
}

/** 달력 셀 (일~토, 인접월 포함) */
export function buildMonthCells(viewYear, viewMonth0) {
  const first = new Date(viewYear, viewMonth0, 1);
  const startPad = first.getDay(); // 0=일
  const cells = [];
  const start = new Date(viewYear, viewMonth0, 1 - startPad);
  for (let i = 0; i < 42; i += 1) {
    const dt = new Date(start);
    dt.setDate(start.getDate() + i);
    const stor = formatDate(dt);
    cells.push({
      date: dt,
      storage: stor,
      day: dt.getDate(),
      inMonth: dt.getMonth() === viewMonth0,
      isToday: stor === todayStorage(),
    });
  }
  return cells;
}
